import { Link, useHistory, useLocation } from 'react-router-dom';
import './Navbar.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './auth/AuthContext';
import { supabase } from './auth/supabaseClient';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'https://helpmediy.org:3001/api';

// Use react routing links.
// React intercepts the calls to the server and routes them itself.
const Navbar = () => {
  const { user, isAdmin, signOut } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const navbarRef = useRef(null);
  const navigationButtonRef = useRef(null);
  const userButtonRef = useRef(null);
  const notificationButtonRef = useRef(null);

  const closePanels = useCallback(() => {
    setNavigationOpen(false);
    setMenuOpen(false);
    setNotificationsOpen(false);
  }, []);

  useEffect(() => { closePanels(); }, [location, user, isAdmin, closePanels]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1100px)');
    const handleResize = () => closePanels();
    desktop.addEventListener('change', handleResize);
    return () => desktop.removeEventListener('change', handleResize);
  }, [closePanels]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const initials = user?.email
    ? user.email
        .split('@')[0]
        .split(/[.\-_]/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('')
    : '?';

  const unreadCount = useMemo(
    () => notifications.filter((notice) => !notice.read).length,
    [notifications]
  );

  const fetchNotifications = useCallback(async () => {
    if (!user || !isAdmin) return;
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return;
    const res = await fetch(`${API_BASE}/notifications`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const payload = await res.json();
    const list = payload.notifications || [];
    const seen = new Set();
    const deduped = [];
    list.forEach((notice) => {
      if (!notice?._id) return;
      if (seen.has(notice._id)) return;
      seen.add(notice._id);
      deduped.push(notice);
    });
    setNotifications(deduped);
  }, [user, isAdmin]);

  const markNotificationsRead = async (ids) => {
    if (!user || !isAdmin) return;
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return;
    await fetch(`${API_BASE}/notifications/read`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });
  };

  const markAllNotificationsRead = async () => {
    if (!user || !isAdmin) return;
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return;
    await fetch(`${API_BASE}/notifications/read`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ all: true }),
    });
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user || !isAdmin) return undefined;
    let stream;
    const connect = async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return;
      stream = new EventSource(
        `${API_BASE}/notifications/stream?token=${encodeURIComponent(token)}`
      );
      stream.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'notification') {
            setNotifications((prev) => {
              if (!payload.notification?._id) return prev;
              if (payload.notification.read) return prev.filter((item) => item._id !== payload.notification._id);
              if (prev.some((item) => item._id === payload.notification._id)) {
                return prev.map((item) => item._id === payload.notification._id ? payload.notification : item);
              }
              return [payload.notification, ...prev];
            });
          }
        } catch (err) {
          // ignore parse errors
        }
      };
    };
    connect();
    return () => {
      if (stream) stream.close();
    };
  }, [user, isAdmin]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (!navbarRef.current?.contains(event.target)) closePanels();
      else {
        if (!notificationRef.current?.contains(event.target)) setNotificationsOpen(false);
        if (!userMenuRef.current?.contains(event.target)) setMenuOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key !== 'Escape') return;
      const trigger = notificationsOpen ? notificationButtonRef : menuOpen ? userButtonRef : navigationOpen ? navigationButtonRef : null;
      if (trigger) {
        event.preventDefault();
        closePanels();
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    document.addEventListener('focusin', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('pointerdown', handleOutside);
      document.removeEventListener('focusin', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen, notificationsOpen, navigationOpen, closePanels]);

  return (
    <nav className="navbar site-navbar" aria-label="Main navigation" ref={navbarRef}>
      <h1 className="navbar-brand-title"><Link className="navbar-brand-link" to="/" onClick={closePanels}>Help Me DIY</Link></h1>
      <div className="navbar-actions">
        <button type="button" className="navbar-menu-toggle" ref={navigationButtonRef}
          aria-label="Navigation menu" aria-expanded={navigationOpen} aria-controls="navbar-links"
          onClick={() => { setNavigationOpen((open) => !open); setMenuOpen(false); setNotificationsOpen(false); }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            {navigationOpen ? <path d="m6 6 12 12M6 18 18 6" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
        {user && isAdmin && (
          <div className="notification-menu" ref={notificationRef}>
            <button type="button" className="notification-button" ref={notificationButtonRef}
              aria-label={`Notifications, ${unreadCount} unread`} aria-expanded={notificationsOpen} aria-controls="navbar-notifications"
              onClick={() => { setNotificationsOpen((open) => !open); setMenuOpen(false); setNavigationOpen(false); }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
              </svg>
              {unreadCount > 0 && <span className="notification-badge" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>
            {notificationsOpen && (
              <section className="notification-dropdown" id="navbar-notifications" aria-label="Notifications">
                <div className="notification-header">
                  <span>Notifications</span>
                  <button type="button" className="notification-mark-all" onClick={() => { setNotifications([]); markAllNotificationsRead(); }}>Mark all read</button>
                </div>
                {notifications.length === 0 && <div className="notification-empty">No notifications yet.</div>}
                {notifications.map((notice) => (
                  <button key={notice._id} type="button" className={`notification-item ${notice.read ? 'read' : 'unread'}`}
                    onClick={() => {
                      if (notice.read) return;
                      setNotifications((prev) => prev.filter((item) => item._id !== notice._id));
                      markNotificationsRead([notice._id]);
                      if (notice.data?.url) { closePanels(); history.push(notice.data.url); }
                    }}>
                    <strong>{notice.title}</strong>
                    <span>{notice.message}</span>
                    <span className="notification-time">{notice.createdAt ? new Date(notice.createdAt).toLocaleString() : ''}</span>
                  </button>
                ))}
              </section>
            )}
          </div>
        )}
        <div className="user-menu" ref={userMenuRef}>
          <button type="button" className="user-avatar" ref={userButtonRef}
            onClick={() => { setMenuOpen((open) => !open); setNotificationsOpen(false); setNavigationOpen(false); }}
            aria-label="User menu" aria-expanded={menuOpen} aria-controls="navbar-user">
            {initials}
          </button>
          {menuOpen && (
            <div className="user-dropdown" id="navbar-user">
              {user ? <>
                <div className="user-email">Signed in as<span>{user.email}</span></div>
                <button type="button" onClick={() => { signOut(); closePanels(); }}>Log out</button>
              </> : <Link to="/login" onClick={closePanels}>Log in</Link>}
            </div>
          )}
        </div>
      </div>
      <div id="navbar-links" className={`links navbar-links${navigationOpen ? ' is-open' : ''}`} onClick={(event) => { if (event.target.closest('a')) closePanels(); }}>
        <Link to="/">Home</Link>
        <Link to="/raffle">Raffle</Link>
        <Link to="/parts">Porsche Parts</Link>
        <Link to="/merch">Merch</Link>
        <Link to="/eeprom">EEPROM</Link>
        {isAdmin && <Link to="/admin">Admin</Link>}
        <Link to="/about">About</Link>
      </div>
    </nav>
  );
};

export default Navbar;
